import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse,
  HttpContextToken,
} from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { NetworkService } from '../services/network.service';
import { OfflineQueueService } from '../services/offline-queue.service';
import { OfflineCacheService } from '../services/offline-cache.service';
import { LoggerService } from '../services/logger.service';

/**
 * If true, the OfflineInterceptor will NEVER queue this request.
 * (Useful for apps where offline queueing would create "fake success" flows.)
 *
 * IMPORTANT: Uses HttpContext (not headers) so it doesn't affect CORS preflights.
 */
export const NO_OFFLINE_QUEUE = new HttpContextToken<boolean>(() => false);

/**
 * Interceptor that handles offline scenarios:
 * - Queues non-GET requests when offline
 * - Serves cached responses for GET requests when offline
 * - Caches successful GET responses
 * - Automatically syncs queued requests when back online
 */
@Injectable()
export class OfflineInterceptor implements HttpInterceptor {
  private readonly networkService = inject(NetworkService);
  private readonly queueService = inject(OfflineQueueService);
  private readonly cacheService = inject(OfflineCacheService);
  private readonly logger = inject(LoggerService);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isOnline = this.networkService.isCurrentlyOnline();
    const method = req.method.toUpperCase();
    const url = req.url;
    const isGetRequest = method === 'GET';
    const isAuthEndpoint = url.includes('/auth/');
    const noQueue = req.context.get(NO_OFFLINE_QUEUE);

    // Extract query params for cache key
    const params: Record<string, any> = {};
    req.params.keys().forEach((key) => {
      params[key] = req.params.get(key);
    });

    // If offline and GET request, try to serve from cache
    if (!isOnline && isGetRequest) {
      const cached = this.cacheService.get(url, params);
      if (cached) {
        this.logger.debug(`[OfflineInterceptor] Serving cached response for ${url}`);
        // Return cached data as HTTP response
        // Use HttpEvent type 4 (Response) - need to create proper HttpResponse
        return of(new HttpResponse({
          body: cached,
          status: 200,
          statusText: 'OK (Cached)',
          url: req.url,
          headers: req.headers,
        }) as HttpEvent<any>);
      } else {
        this.logger.debug(`[OfflineInterceptor] No cache available for ${url}, returning error`);
        return throwError(() => new HttpErrorResponse({
          error: { message: 'No internet connection and no cached data available' },
          status: 0,
          statusText: 'Offline',
          url: req.url,
        }));
      }
    }

    // If offline and non-GET request, queue it
    if (!isOnline && !isGetRequest) {
      // CRITICAL: Never queue auth requests (OTP/login). They cannot be replayed safely.
      if (isAuthEndpoint || noQueue) {
        this.logger.debug(`[OfflineInterceptor] Not queueing auth request while offline: ${method} ${url}`);
        return throwError(() => new HttpErrorResponse({
          error: { message: 'No internet connection. Please try again online.' },
          status: 0,
          statusText: 'Offline',
          url: req.url,
        }));
      }

      this.logger.debug(`[OfflineInterceptor] Queueing ${method} request for ${url}`);
      
      // Determine priority based on endpoint
      const priority = this.determinePriority(url, method);
      
      const requestId = this.queueService.queueRequest(req, priority);
      
      // Return a success response to the caller (request is queued)
      return of(new HttpResponse({
        body: { 
          queued: true, 
          requestId,
          message: 'Request queued for when connection is restored'
        },
        status: 202, // Accepted
        statusText: 'Queued',
        url: req.url
      }));
    }

    // Online: proceed with request
    return next.handle(req).pipe(
      tap((event) => {
        // Cache successful GET responses
        if (isGetRequest && event instanceof HttpResponse) {
          const ttl = this.getCacheTTL(url);
          if (ttl > 0) {
            this.cacheService.set(url, event.body, ttl, params);
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // If network error and online status says we're online, 
        // it might be a temporary network issue
        if (this.isNetworkError(error) && this.networkService.isCurrentlyOnline()) {
          // For non-GET requests, queue them
          if (!isGetRequest) {
            // CRITICAL: Never queue auth requests (OTP/login). Surface the error to the UI instead.
            if (isAuthEndpoint || noQueue) {
              this.logger.debug(`[OfflineInterceptor] Not queueing auth request on network error: ${method} ${url}`);
              return throwError(() => error);
            }

            this.logger.debug(`[OfflineInterceptor] Network error, queueing ${method} request for ${url}`);
            const priority = this.determinePriority(url, method);
            const requestId = this.queueService.queueRequest(req, priority);
            
            return of(new HttpResponse({
              body: { 
                queued: true, 
                requestId,
                message: 'Request queued due to network error'
              },
              status: 202,
              statusText: 'Queued',
              url: req.url
            }));
          }
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Determine request priority based on endpoint and method
   */
  private determinePriority(url: string, method: string): 'critical' | 'normal' | 'low' {
    // Critical: Order creation, payment, status updates
    if (
      (url.includes('/orders') && (method === 'POST' || method === 'PATCH')) ||
      url.includes('/payments') ||
      url.includes('/status')
    ) {
      return 'critical';
    }

    // Low: Analytics, search, non-critical updates
    if (
      url.includes('/analytics') ||
      url.includes('/search') ||
      url.includes('/ratings')
    ) {
      return 'low';
    }

    // Normal: Everything else
    return 'normal';
  }

  /**
   * Get cache TTL for a URL (in milliseconds)
   * Returns 0 to skip caching (e.g. large payloads that would exceed localStorage quota).
   */
  private getCacheTTL(url: string): number {
    // Do not cache /api/products - response can be very large and cause QuotaExceededError
    if (url.includes('/products')) {
      return 0;
    }
    // High priority data: cache for longer
    if (url.includes('/vendors') || url.includes('/categories')) {
      return 2 * 60 * 60 * 1000; // 2 hours
    }

    // Medium priority: 1 hour
    if (url.includes('/orders') || url.includes('/promotions')) {
      return 60 * 60 * 1000; // 1 hour
    }

    // Low priority: 30 minutes
    if (url.includes('/notifications') || url.includes('/reviews')) {
      return 30 * 60 * 1000; // 30 minutes
    }

    // Default: 15 minutes
    return 15 * 60 * 1000; // 15 minutes
  }

  /**
   * Check if error is a network error
   */
  private isNetworkError(error: HttpErrorResponse): boolean {
    return (
      error.status === 0 || // Network error
      error.status === 504 || // Gateway timeout
      error.status === 503 || // Service unavailable
      (error as any).name === 'TimeoutError' ||
      error.message?.includes('network') ||
      error.message?.includes('timeout') ||
      error.message?.includes('Failed to fetch')
    );
  }
}

